import { IPassageWaveformSummary } from '@/models/PassageWaveform';

/**
 * EN-1851: row helpers shared by PassagesTable.vue, the View Passages dialog
 * (PipelineSectionMarkerPassagesDialog.vue) and the tracksheet run passages
 * section. Moved unchanged from the dialog.
 */

interface PassageRowLike {
  passageWaveforms?: IPassageWaveformSummary[] | null;
}

/** Waveforms with an ML prediction and confidence (the ML column's badges). */
export function getMLWaveforms(row: PassageRowLike): IPassageWaveformSummary[] {
  return (row.passageWaveforms || []).filter(
    (wf) => wf.mlPrediction != null && wf.mlConfidence != null
  );
}

/** A manual passage has exactly one waveform, of type 'H'. */
export function isManualPassage(row: PassageRowLike): boolean {
  const wfs = row.passageWaveforms || [];
  return wfs.length === 1 && wfs[0].passageWaveformType === 'H';
}

/** -1 (confidently red) .. 1 (confidently green). */
export function getMLMean(prediction: boolean, confidence: number): number {
  return (2 * Number(prediction) - 1) * confidence;
}

export function getMLColor(prediction: boolean, confidence: number): string {
  const mean = getMLMean(prediction, confidence);
  if (mean > 0.89) return 'green';
  if (mean > 0.75) return 'green-4';
  if (mean > -0.25) return 'orange';
  if (mean > -0.5) return 'red-3';
  return 'red';
}

export function waveformMLColor(wf: IPassageWaveformSummary): string {
  return getMLColor(wf.mlPrediction ?? false, wf.mlConfidence ?? 0);
}

/** The "green ML results only" filter: any ML waveform shown green. */
export function hasGreenML(row: PassageRowLike): boolean {
  return getMLWaveforms(row).some((wf) =>
    waveformMLColor(wf).includes('green')
  );
}

/**
 * EN-1851: on a phone the Waveforms column is hidden, so a row click opens the
 * row's first waveform in the chart; none for a manual passage or no waveform.
 */
export function rowChartWaveform(
  row: PassageRowLike
): IPassageWaveformSummary | null {
  if (isManualPassage(row)) return null;
  return (row.passageWaveforms || [])[0] ?? null;
}

/** A click on a button, link or input in the row is that control's, not the row's. */
export function isControlClick(
  target: { closest?: (selector: string) => unknown } | null | undefined
): boolean {
  return !!target?.closest?.('button, a, input, .q-btn');
}

/**
 * EN-1851: the q-table listeners for a row click that opens the chart (phone),
 * none otherwise. For `v-on="..."` on the q-table.
 */
export function rowClickListeners(
  opensChart: boolean,
  onRowClick: (...args: any[]) => void
): Record<string, (...args: any[]) => void> {
  // camelCase: q-table emits 'rowClick', and Vue finds only onRowClick for it.
  // 'row-click' here would become onRow-click and never be called.
  return opensChart ? { rowClick: onRowClick } : {};
}
